import { useCallback, useEffect, useState } from 'react';
import { KeyRound, Loader2, Plus, ShieldCheck, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';

import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const ROLES = [
  { value: 'admin', label: 'Administrator' },
  { value: 'manager', label: 'Manager' },
  { value: 'staff', label: 'Staff' },
  { value: 'viewer', label: 'Viewer' },
];

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setUsers((await api.get('/users')).data); }
    catch (error) { toast.error(error?.response?.data?.detail || 'Gagal memuat user.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (target, changes) => {
    try {
      await api.put(`/users/${target.id}`, changes);
      toast.success('User diperbarui.');
      load();
    } catch (error) { toast.error(error?.response?.data?.detail || 'User gagal diperbarui.'); }
  };

  return (
    <div className="animate-in-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-[var(--fpccs-primary)]" /> Manajemen User</h1>
          <p className="text-sm text-muted-fpccs mt-1">Atur akses berdasarkan tanggung jawab. Perubahan role langsung mencabut sesi lama.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-[var(--fpccs-primary)] text-[var(--fpccs-bg)] hover:bg-[var(--fpccs-primary-2)]"><Plus className="w-4 h-4 mr-1.5" /> Tambah User</Button>
      </div>

      <div className="card-fpccs overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? [...Array(4)].map((_, index) => <TableRow key={index}>{[...Array(5)].map((__, cell) => <TableCell key={cell}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>) : users.map((item) => {
              const isSelf = item.id === currentUser?.id;
              return <TableRow key={item.id}>
                <TableCell className="font-medium">{item.name}{isSelf && <span className="ml-2 text-[10px] text-muted-fpccs">Anda</span>}</TableCell>
                <TableCell className="text-xs text-muted-fpccs">{item.email}</TableCell>
                <TableCell className="min-w-[150px]"><Select value={item.role} disabled={isSelf} onValueChange={(role) => update(item, { role })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{ROLES.map((role) => <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>)}</SelectContent></Select></TableCell>
                <TableCell><span className={`text-[11px] px-2 py-1 rounded border ${item.is_active ? 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' : 'text-red-400 border-red-500/30 bg-red-500/10'}`}>{item.is_active ? 'Aktif' : 'Nonaktif'}</span></TableCell>
                <TableCell><div className="flex justify-end gap-1"><Button size="icon" variant="ghost" title="Reset password" onClick={() => setResetTarget(item)}><KeyRound className="w-4 h-4" /></Button><Button size="icon" variant="ghost" disabled={isSelf} title={item.is_active ? 'Nonaktifkan' : 'Aktifkan'} onClick={() => update(item, { is_active: !item.is_active })}>{item.is_active ? <UserX className="w-4 h-4 text-red-400" /> : <UserCheck className="w-4 h-4 text-emerald-400" />}</Button></div></TableCell>
              </TableRow>;
            })}
          </TableBody>
        </Table>
      </div>

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={load} />
      <ResetPasswordDialog user={resetTarget} onOpenChange={(open) => !open && setResetTarget(null)} />
    </div>
  );
}

function CreateUserDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ name: '', email: '', password: '', role: 'staff' }); }, [open]);
  const submit = async (event) => {
    event.preventDefault(); setSaving(true);
    try { await api.post('/users', form); toast.success('User berhasil dibuat.'); onOpenChange(false); onSaved(); }
    catch (error) { toast.error(error?.response?.data?.detail || 'User gagal dibuat.'); }
    finally { setSaving(false); }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="bg-[var(--fpccs-surface-1)]"><DialogHeader><DialogTitle>Tambah User</DialogTitle></DialogHeader><form onSubmit={submit} className="space-y-3"><Field label="Nama"><Input required maxLength={200} value={form.name} onChange={(event) => setForm((old) => ({ ...old, name: event.target.value }))} /></Field><Field label="Email"><Input required type="email" value={form.email} onChange={(event) => setForm((old) => ({ ...old, email: event.target.value }))} /></Field><Field label="Password sementara"><Input required type="password" minLength={12} maxLength={128} value={form.password} onChange={(event) => setForm((old) => ({ ...old, password: event.target.value }))} /></Field><p className="text-xs text-muted-fpccs">Minimal 12 karakter dan sedikitnya tiga jenis karakter.</p><Field label="Role"><Select value={form.role} onValueChange={(role) => setForm((old) => ({ ...old, role }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{ROLES.map((role) => <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>)}</SelectContent></Select></Field><DialogFooter><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Batal</Button><Button type="submit" disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buat User'}</Button></DialogFooter></form></DialogContent></Dialog>;
}

function ResetPasswordDialog({ user, onOpenChange }) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (user) setPassword(''); }, [user]);
  const submit = async (event) => {
    event.preventDefault(); setSaving(true);
    try { await api.post(`/users/${user.id}/reset-password`, { new_password: password }); toast.success('Password direset dan semua sesi user dicabut.'); onOpenChange(false); }
    catch (error) { toast.error(error?.response?.data?.detail || 'Password gagal direset.'); }
    finally { setSaving(false); }
  };
  return <Dialog open={!!user} onOpenChange={onOpenChange}><DialogContent className="bg-[var(--fpccs-surface-1)]"><DialogHeader><DialogTitle>Reset Password {user?.name}</DialogTitle></DialogHeader><form onSubmit={submit} className="space-y-3"><Field label="Password baru"><Input required type="password" minLength={12} maxLength={128} value={password} onChange={(event) => setPassword(event.target.value)} /></Field><p className="text-xs text-muted-fpccs">Tindakan ini langsung mencabut seluruh token login user tersebut.</p><DialogFooter><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Batal</Button><Button type="submit" disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Password'}</Button></DialogFooter></form></DialogContent></Dialog>;
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs text-muted-fpccs">{label}</Label>{children}</div>;
}
